'use client';
import React, { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import SectionHeader from '@/components/SectionHeader';
import { Clock, Film, Tv, Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useRealtimeContent } from '@/hooks/useRealtimeContent';

interface RecentMovie {
  id: string;
  title: string;
  slug: string;
  genre: string;
  year: number;
  poster_url: string;
  created_at: string;
}

interface RecentSeries {
  id: string;
  title: string;
  slug: string;
  genre: string;
  year: number;
  poster_url: string;
  created_at: string;
}

export default function RecentlyAddedSection() {
  const [recentMovies, setRecentMovies] = useState<RecentMovie[]>([]);
  const [recentSeries, setRecentSeries] = useState<RecentSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchRecent = useCallback(async () => {
    const [moviesRes, seriesRes] = await Promise.all([
      supabase
        .from('movies')
        .select('id, title, slug, genre, year, poster_url, created_at')
        .order('created_at', { ascending: false })
        .limit(4),
      supabase
        .from('series')
        .select('id, title, slug, genre, year, poster_url, created_at')
        .order('created_at', { ascending: false })
        .limit(4),
    ]);
    if (!moviesRes.error && moviesRes.data) setRecentMovies(moviesRes.data as RecentMovie[]);
    if (!seriesRes.error && seriesRes.data) setRecentSeries(seriesRes.data as RecentSeries[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchRecent();
  }, [fetchRecent]);

  useRealtimeContent({
    tables: ['movies', 'series'],
    onAnyChange: () => fetchRecent(),
  });

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  return (
    <section className="pb-8">
      <SectionHeader
        title="Recently Added"
        subtitle="Latest uploads to NeonCine"
        accentColor="purple"
      />
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-accent" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Movies */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Film size={16} className="text-primary" />
              <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Movies</span>
            </div>
            {recentMovies.length === 0 ? (
              <div className="glass-card border border-border rounded-xl p-6 text-center">
                <p className="text-xs text-muted-foreground font-body">No movies added yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentMovies.map((movie) => (
                  <Link key={`recent-movie-${movie.id}`} href={`/movie-detail-page?slug=${movie.slug}`} className="block group">
                    <div className="glass-card glass-card-hover rounded-xl p-3 flex items-center gap-3">
                      <div className="w-10 h-14 rounded-lg overflow-hidden flex-shrink-0">
                        <AppImage
                          src={movie.poster_url || '/assets/images/no_image.png'}
                          alt={`${movie.title} thumbnail`}
                          width={40}
                          height={56}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                          {movie.title}
                        </p>
                        <p className="text-xs text-muted-foreground font-body">{movie.genre} · {movie.year}</p>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground flex-shrink-0">
                        <Clock size={11} />
                        <span className="text-[10px] font-mono-data">{formatDate(movie.created_at)}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Recent Series */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Tv size={16} className="text-accent" />
              <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Series</span>
            </div>
            {recentSeries.length === 0 ? (
              <div className="glass-card border border-border rounded-xl p-6 text-center">
                <p className="text-xs text-muted-foreground font-body">No series added yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {recentSeries.map((series) => (
                  <Link key={`recent-series-${series.id}`} href={`/series/${series.slug}`} className="block group">
                    <div className="glass-card glass-card-hover rounded-xl p-3 flex items-center gap-3">
                      <div className="w-10 h-14 rounded-lg overflow-hidden flex-shrink-0">
                        <AppImage
                          src={series.poster_url || '/assets/images/no_image.png'}
                          alt={`${series.title} thumbnail`}
                          width={40}
                          height={56}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-foreground truncate group-hover:text-accent transition-colors">
                          {series.title}
                        </p>
                        <p className="text-xs text-muted-foreground font-body">{series.genre} · {series.year}</p>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground flex-shrink-0">
                        <Clock size={11} />
                        <span className="text-[10px] font-mono-data">{formatDate(series.created_at)}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}